import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AccountSection = ({ 
  userProfile, 
  onUpdateProfile, 
  onChangePassword,
  className = '' 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [profileData, setProfileData] = useState({
    name: userProfile?.name || "John Smith",
    email: userProfile?.email || "john.smith@example.com",
    phone: userProfile?.phone || "+1 (555) 123-4567",
    timezone: userProfile?.timezone || "America/New_York"
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleProfileSave = async () => {
    try {
      await onUpdateProfile(profileData);
      setIsEditing(false);
    } catch (error) {
      console.error('Profile update failed:', error);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    
    try {
      await onChangePassword(passwordData);
      setIsChangingPassword(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Password change failed:', error);
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Profile Information */}
      <div className="bg-surface border border-border rounded-lg p-4 lg:p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-text-primary">Profile Information</h4>
          <Button
            variant={isEditing ? "outline" : "default"}
            size="sm"
            onClick={() => setIsEditing(!isEditing)}
            iconName={isEditing ? "X" : "Edit"}
            iconPosition="left"
          >
            {isEditing ? 'Cancel' : 'Edit'}
          </Button>
        </div>

        <div className="space-y-4">
          <Input
            label="Full Name"
            type="text"
            value={profileData?.name}
            onChange={(e) => setProfileData({ ...profileData, name: e?.target?.value })}
            disabled={!isEditing}
          />
          
          <Input
            label="Email Address"
            type="email"
            value={profileData?.email}
            onChange={(e) => setProfileData({ ...profileData, email: e?.target?.value })}
            disabled={!isEditing}
          />
          
          <Input
            label="Phone Number"
            type="tel"
            value={profileData?.phone}
            onChange={(e) => setProfileData({ ...profileData, phone: e?.target?.value })}
            disabled={!isEditing}
          />
          
          <Input
            label="Timezone"
            type="text"
            value={profileData?.timezone}
            onChange={(e) => setProfileData({ ...profileData, timezone: e?.target?.value })}
            disabled={!isEditing}
          />

          {isEditing && (
            <div className="flex space-x-3 pt-2">
              <Button
                variant="default"
                size="sm"
                onClick={handleProfileSave}
                iconName="Check"
                iconPosition="left"
              >
                Save Changes
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
      {/* Password Change */}
      <div className="bg-surface border border-border rounded-lg p-4 lg:p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-text-primary">Change Password</h4>
          <Button
            variant={isChangingPassword ? "outline" : "default"}
            size="sm"
            onClick={() => setIsChangingPassword(!isChangingPassword)}
            iconName={isChangingPassword ? "X" : "Lock"}
            iconPosition="left"
          >
            {isChangingPassword ? 'Cancel' : 'Change Password'}
          </Button>
        </div>

        {isChangingPassword && (
          <div className="space-y-4">
            <Input
              label="Current Password"
              type="password"
              value={passwordData?.currentPassword}
              onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e?.target?.value })}
              placeholder="Enter current password"
            />
            
            <Input
              label="New Password"
              type="password"
              value={passwordData?.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e?.target?.value })}
              placeholder="Enter new password"
            />
            
            <Input
              label="Confirm New Password"
              type="password"
              value={passwordData?.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e?.target?.value })}
              placeholder="Confirm new password"
            />

            <div className="flex space-x-3 pt-2">
              <Button
                variant="default"
                size="sm"
                onClick={handlePasswordChange}
                iconName="Check"
                iconPosition="left"
              >
                Update Password
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsChangingPassword(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {!isChangingPassword && (
          <p className="text-sm text-text-secondary">
            Last changed: November 15, 2024
          </p>
        )}
      </div>
      {/* Subscription Info */}
      <div className="bg-surface border border-border rounded-lg p-4 lg:p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-text-primary">Subscription</h4>
          <div className="flex items-center space-x-2">
            <Icon name="Crown" size={16} className="text-warning" />
            <span className="text-sm font-medium text-warning">Pro Plan</span>
          </div>
        </div>
        
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-text-secondary">Plan:</span>
            <span className="text-text-primary font-medium">ContactSync Pro</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-text-secondary">Next billing:</span>
            <span className="text-text-primary font-medium">January 12, 2025</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-text-secondary">Amount:</span>
            <span className="text-text-primary font-medium">$9.99/month</span>
          </div>
        </div>

        <div className="flex space-x-3 mt-4 pt-4 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            iconName="CreditCard"
            iconPosition="left"
          >
            Update Payment
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
          >
            Download Invoice
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccountSection;